import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { useSdk } from '../../../../../sdk';
import { ContactAvatar } from '../../../../../components/contact-avatar';
import { AddContactButton } from '../../../../../components/buttons/add-contact-button';
import { ContactSource } from '@zaplify/services/user-contacts/shared';
import { Checkbox } from '@shadcn/ui/components/ui/checkbox';
import { BulkAddContactsButton } from '../../../../../components/bulk-add-contact-button';
import { cn } from '@shadcn/ui/lib/utils';
import { GET_USER_CONTACT_BY_PROSPECT_ID, GET_USER_CONTACTS_BY_PROSPECT_IDS } from '@zaplify/graphql';
import { useQuery as useApolloQuery } from '@apollo/client';
import { PlaybookBadge } from '../../../../../components/playbook-badge';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@shadcn/ui/components/ui/hover-card';
import { ContactCard } from '../../../../../components/contact-card/contact-card';
import { ExternalLinkIcon, HamIcon, Linkedin, MessageSquare } from 'lucide-react';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@shadcn/ui/components/ui/tooltip';
import { ActionCard } from '../../../../action-feed/components/cards/action-feed-card';
import { Button } from '@shadcn/ui/components/ui/button';
import { findProspectData } from '../../../../../sdk/internal/prospect.sdk';
import { getLinkedinProfilesSummary } from '../../../../../sdk/internal/linkedin-profiles.sdk';
import { isMongoId } from '@zaplify/utils';
export const DisplayContactsFromIdsUI = ({
    profiles,
    status,
    className,
}: {
    profiles: { linkedinProfileUrl: string | null; linkedinUserId: string | null; prospectId: string | null }[];
    status: 'running' | 'complete' | 'incomplete' | 'requires-action';
    className?: string;
}) => {
    if (status === 'running') {
        return <ContactTable isRunning className={className} />;
    }
    if (status === 'complete' && profiles) {
        return <ContactTable profiles={profiles} className={className} />;
    }

    return <div>Error</div>;
};

interface ContactRowProfile {
    linkedinProfileImgUrl: string;
    fullName: string;
    headline: string;
    linkedinProfileUrl: string;
    linkedinUserId: string;
    prospectId?: string;
    userContactId?: string;
}

interface ContactRowProps {
    linkedinUserId?: string;
    prospectId?: string;
    linkedinProfileUrl?: string;
    isSelected?: boolean;
    onToggleSelect?: () => void;
    setSelectedProspectId?: (prospectId: string) => void;
    disabled?: boolean;
    isLastRow?: boolean;
    profile: ContactRowProfile;
}

const ContactRowContent = ({
    linkedinUserId,
    prospectId,
    linkedinProfileUrl,
    isSelected,
    onToggleSelect,
    setSelectedProspectId,
    disabled,
    isLastRow,
    profile,
}: ContactRowProps) => {
    if (!profile) {
        return null;
    }

    return (
        <div
            className={cn(
                'flex bg-background-primary items-center gap-4 px-4 py-3 rounded-xl transition-colors group hover:bg-muted',
                isLastRow && 'mb-0'
            )}
        >
            <Checkbox checked={!!isSelected} onCheckedChange={onToggleSelect} className="mr-2" />
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <HoverCard openDelay={1000} closeDelay={100}>
                    <HoverCardTrigger asChild>
                        <ContactAvatar
                            avatarUrl={profile.linkedinProfileImgUrl}
                            fullName={profile.fullName}
                            size="md"
                        />
                    </HoverCardTrigger>
                    <HoverCardTrigger asChild>
                        <div className="flex flex-col min-w-0">
                            <div className="flex flex-row items-center gap-2 group/name">
                                <span className="font-medium truncate max-w-[180px] md:max-w-[260px]">
                                    {profile.fullName}
                                </span>
                                {profile.linkedinProfileUrl && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <a
                                                    href={profile.linkedinProfileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Linkedin className="w-4 h-4 opacity-0 group-hover/name:opacity-100 transition-opacity text-text-secondary-hover" />
                                                </a>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Open in new LinkedIn tab</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                            <span className="text-sm text-muted-foreground truncate ">{profile.headline}</span>
                        </div>
                    </HoverCardTrigger>
                    <HoverCardContent
                        side="right"
                        align="center"
                        sideOffset={12}
                        className="z-100 w-fit p-0 rounded-xl border-none shadow-none bg-transparent"
                    >
                        <ContactCard linkedinUserId={profile.linkedinUserId ?? ''} prospectId={profile.prospectId} />
                    </HoverCardContent>
                </HoverCard>
            </div>

            <div className="ml-auto">
                {profile.userContactId ? (
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => setSelectedProspectId?.(profile.prospectId)}>
                            <MessageSquare className="w-4 h-4 text-text-secondary" />
                        </Button>
                        <PlaybookBadge prospectId={profile.prospectId} size="small" />
                    </div>
                ) : (
                    <AddContactButton
                        linkedinUserId={profile.linkedinUserId}
                        linkedinUrl={profile.linkedinProfileUrl}
                        userContactId={profile.userContactId}
                        source={ContactSource.Agent}
                        variant="outline"
                        size="sm"
                    />
                )}
            </div>
        </div>
    );
};

interface ContactTableProps {
    profiles?: { linkedinProfileUrl?: string; linkedinUserId?: string; prospectId?: string }[];
    isRunning?: boolean;
    className?: string;
}

const ContactTable = ({ profiles, isRunning, className }: ContactTableProps) => {
    const linkedinUserIds = useMemo(() => {
        const linkedinIds = profiles?.map((p) => p.linkedinUserId).filter((id): id is string => !!id) ?? [];

        const linkedinIdsFromProspectIds =
            profiles
                ?.map((p) => p.prospectId)
                ?.filter((prospectId) => prospectId?.length === 39 && prospectId?.startsWith('ACoAA')) ?? [];

        return [...linkedinIds, ...linkedinIdsFromProspectIds];
    }, [profiles]);

    const linkedinPublicIdentifiers = useMemo(() => {
        return profiles?.map((p) => p.linkedinProfileUrl?.split('/in/')?.[1]).filter((id): id is string => !!id);
    }, [profiles]);

    const prospectIds = useMemo(() => {
        const prospectIds = profiles?.map((p) => p.prospectId).filter((id): id is string => !!id) ?? [];

        const prospectIdsFromLinkedinIds =
            profiles?.map((p) => p.linkedinUserId)?.filter((linkedinId) => isMongoId(linkedinId)) ?? [];
        return [...prospectIds, ...prospectIdsFromLinkedinIds];
    }, [profiles]);

    const {
        linkedinProfiles: { getLinkedinProfileByMemberIdOrPublicIdentifier },
        prospect: { findProspectData },
    } = useSdk();

    const { data: linkedinProfilesSummary, isLoading: linkedinProfilesSummaryLoading } = useQuery(
        getLinkedinProfilesSummary({
            memberIds: linkedinUserIds,
            publicIdentifiers: linkedinPublicIdentifiers,
            pickFields: ['fullName', 'headline', 'profileImageUrl', 'publicIdentifier', 'memberId'],
            limit: 500,
            skip: 0,
        })
    );

    const { data: prospects, isLoading: prospectsLoading } = useQuery(
        findProspectData(
            {
                prospectIds: prospectIds,
                page: 1,
                perPage: 500,
                includeFields: [
                    'fullName',
                    'linkedinProfileImgUrl',
                    'occupationTitle',
                    'organizationName',
                    'linkedinUserId',
                    'organizationId',
                ],
            },
            { enabled: !!prospectIds?.length }
        )
    );

    const { data: userContacts, loading: userContactsLoading } = useApolloQuery(GET_USER_CONTACTS_BY_PROSPECT_IDS, {
        variables: { prospectIds: prospectIds },
        skip: !prospectIds?.length,
    });

    /**
     * Since we have three sources of profiles, we need to merge them together.
     *
     */
    const mergedProfiles: ContactRowProfile[] = useMemo(() => {
        const merged: ContactRowProfile[] = [];

        const foundLinkedinMembersIndex = new Map<string, number>();
        const foundLinkedinPublicIdentifiersIndex = new Map<string, number>();
        if (linkedinProfilesSummary) {
            linkedinProfilesSummary.map((p, index) => {
                if (foundLinkedinMembersIndex.has(p.memberId)) {
                    return;
                }
                merged.push({
                    linkedinProfileImgUrl: p.profileImageUrl,
                    fullName: p.fullName,
                    headline: p.headline,
                    linkedinProfileUrl: p.publicIdentifier
                        ? `https://www.linkedin.com/in/${p.publicIdentifier || p.memberId}`
                        : undefined,
                    linkedinUserId: p.memberId,
                    prospectId: undefined,
                    userContactId: undefined,
                });
                foundLinkedinMembersIndex.set(p.memberId, index);
                foundLinkedinPublicIdentifiersIndex.set(p.publicIdentifier, index);
            });
        }

        const foundProspectsIndex = new Map<string, number>();
        if (prospects) {
            for (const prospect of prospects?.prospects ?? []) {
                const newProfile = {
                    linkedinProfileImgUrl: prospect.linkedinProfileImgUrl,
                    fullName: prospect.fullName,
                    headline: prospect.occupationTitle,
                    linkedinProfileUrl: prospect.linkedinUserId
                        ? `https://www.linkedin.com/in/${prospect.linkedinUserId}`
                        : undefined,
                    linkedinUserId: prospect.linkedinUserId,
                    prospectId: prospect.prospectId,
                    userContactId: undefined,
                };
                const profileIndex =
                    foundLinkedinMembersIndex.get(prospect.linkedinUserId) ??
                    foundLinkedinPublicIdentifiersIndex.get(prospect.linkedinUserId)!;

                if (profileIndex) {
                    merged[profileIndex] = {
                        ...merged[profileIndex],
                        ...newProfile,
                    };
                    foundProspectsIndex.set(prospect.prospectId, profileIndex);
                } else {
                    merged.push(newProfile);
                    foundLinkedinMembersIndex.set(prospect.linkedinUserId, merged.length - 1);
                    foundLinkedinPublicIdentifiersIndex.set(prospect.linkedinUserId, merged.length - 1);
                    foundProspectsIndex.set(prospect.prospectId, merged.length - 1);
                }
            }
        }

        if (userContacts) {
            for (const userContact of userContacts.UserContacts) {
                const profileIndex = foundProspectsIndex.get(userContact.prospectId);
                if (profileIndex) {
                    merged[profileIndex] = {
                        ...merged[profileIndex],
                        userContactId: userContact.id,
                    };
                }
            }
        }
        return merged.sort((a, b) => {
            // contactId should be first
            if (a.userContactId && !b.userContactId) {
                return -1;
            }
            if (!a.userContactId && b.userContactId) {
                return 1;
            }
            return 0;
        });
    }, [linkedinProfilesSummary, prospects, userContacts]);

    const isLoading = isRunning || linkedinProfilesSummaryLoading || prospectsLoading;

    const cardRef = useRef<HTMLDivElement>(null);
    const toolUIRef = useRef<HTMLDivElement>(null);
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
    const [selectedProspectId, setSelectedProspectId] = useState<string | null>(null);
    const isProfileSelected = useCallback(
        (index: number) => {
            return selectedIndices.includes(index);
        },
        [selectedIndices]
    );

    const toggleSelectProfile = useCallback((index: number) => {
        setSelectedIndices((prev) => {
            if (prev.includes(index)) {
                return prev.filter((i) => i !== index);
            } else {
                return [...prev, index];
            }
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (!mergedProfiles || mergedProfiles.length === 0) return;

        const hasAllSelected = mergedProfiles.length === selectedIndices.length;

        if (hasAllSelected) {
            setSelectedIndices([]);
        } else {
            setSelectedIndices(Array.from({ length: mergedProfiles.length }, (_, i) => i));
        }
    }, [mergedProfiles, selectedIndices.length]);

    const isAllSelected = useMemo(() => {
        if (!mergedProfiles || mergedProfiles.length === 0) return false;
        return mergedProfiles.length === selectedIndices.length;
    }, [mergedProfiles, selectedIndices.length]);

    const selectedCount = useMemo(() => {
        return selectedIndices.length;
    }, [selectedIndices]);

    const selectedContacts = useMemo(() => {
        if (!mergedProfiles) return [];

        return selectedIndices.map((index) => {
            const profile = mergedProfiles[index];
            return {
                personId: profile.prospectId || undefined,
                linkedinUserId: profile.linkedinUserId || undefined,
                linkedinUrl: profile.linkedinProfileUrl || undefined,
            };
        });
    }, [selectedIndices, mergedProfiles]);

    useEffect(() => {
        if (!selectedProspectId) return;

        if (cardRef.current) {
            setTimeout(() => {
                cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
        }

        function handleClickOutside(event: MouseEvent) {
            if (toolUIRef.current) {
                const rect = toolUIRef.current.getBoundingClientRect();
                const clickX = event.clientX;
                const clickY = event.clientY;

                if (
                    clickX < rect.left - 140 ||
                    clickX > rect.right + 140 ||
                    clickY < rect.top ||
                    clickY > rect.bottom
                ) {
                    setSelectedProspectId(null);
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectedProspectId]);

    const skeletonRows = (
        <>
            {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center gap-4 px-4 py-3 rounded-xl">
                    <Skeleton className="h-4 w-4 rounded-sm" />
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 min-w-0">
                        <Skeleton className="h-4 w-32 mb-1" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-9 w-20 rounded-md ml-auto" />
                </div>
            ))}
        </>
    );

    return (
        <div className={cn('w-full flex flex-col gap-4 py-4 animate-fade-in-0.2', className)} ref={toolUIRef}>
            {selectedCount > 0 && (
                <BulkAddContactsButton
                    contacts={selectedContacts}
                    source={ContactSource.Agent}
                    className="w-fit"
                    onAdded={() => {
                        setSelectedIndices([]);
                    }}
                />
            )}
            <div className="max-h-[400px] rounded-xl border bg-background-secondary pb-0 overflow-auto">
                <div className="flex flex-col divide-y divide-border">
                    <div className="flex items-center px-4 py-2 bg-background-secondary sticky top-0 z-10 rounded-t-xl">
                        <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={toggleSelectAll}
                            className="mr-2"
                            disabled={!mergedProfiles || mergedProfiles.length === 0}
                        />
                        <span className="font-semibold text-sm text-muted-foreground flex-1">
                            Contacts {!!mergedProfiles?.length && `(${mergedProfiles.length})`}
                        </span>
                    </div>
                    {isLoading ? (
                        skeletonRows
                    ) : !mergedProfiles || mergedProfiles.length === 0 ? (
                        <div className="h-24 flex items-center justify-center text-center rounded-b-xl">
                            No contacts to display
                        </div>
                    ) : (
                        mergedProfiles.map((profile, rowIndex) => {
                            const { linkedinProfileUrl, linkedinUserId, prospectId } = profile;
                            const isLastRow = rowIndex === mergedProfiles.length - 1;
                            return (
                                <ContactRowContent
                                    key={`${linkedinProfileUrl}-${linkedinUserId}-${prospectId}`}
                                    profile={profile}
                                    linkedinProfileUrl={linkedinProfileUrl}
                                    linkedinUserId={linkedinUserId}
                                    prospectId={prospectId}
                                    isSelected={isProfileSelected(rowIndex)}
                                    setSelectedProspectId={setSelectedProspectId}
                                    onToggleSelect={() => toggleSelectProfile(rowIndex)}
                                    isLastRow={isLastRow}
                                />
                            );
                        })
                    )}
                </div>
            </div>
            {selectedProspectId && (
                <div className="w-full animate-fade-in-0.5">
                    <ActionCard
                        prospectId={selectedProspectId}
                        prospectData={prospects?.prospects?.find((p) => p.prospectId === selectedProspectId)}
                        // baseUrl={`/new/home/${threadId}`}
                        ref={cardRef}
                        isExpanded={!!selectedProspectId}
                        className="max-h-[min(83dvh,1000px)]"
                        isCompleted={false}
                        onClick={() => {}}
                        overrideMotivation={null}
                        overrideMessage={null}
                        hideScheduling
                    />
                </div>
            )}
        </div>
    );
};
